import { loadWebComponents } from '../utils/LoadResources'

const setLoader = (el) => yTos.Cartier.loader.on(el)
const removeLoader = (el) => yTos.Cartier.loader.off(el)

/**
 * @param {HTMLElement} content - MFP content element (.inner)
 * @param {HTMLElement} container - MFP container element (.mfp-container)
 */
const createHeaderPopup = (content, container) => {
	const closePopUpBtn = yTos.Cartier.labels?.closePopUpBtn ?? ''
	const headerEl = document.createElement('div')
	headerEl.classList.add('heading-1')
	headerEl.classList.add('layer-title')
	headerEl.classList.add('mfp-prevent-close')
	headerEl.innerHTML = `
		<span class="mfp-prevent-close" id="layer-title__spanTitle"></span>
        <button aria-label=${closePopUpBtn} class="mfp-close"><span class="icon" aria-hidden="true"></span></button>
	`

	container.querySelector('.mfp-content').insertBefore(headerEl, content)
}

const setHeaderPopupTitle = (container, title) => {
	if (!title) return

	const titleEl = container.querySelector('#layer-title__spanTitle')
	if (titleEl) {
		titleEl.innerText = title
	}
}

const tryResolveHeaderPopupTitle = (container) => {
	const layerTitleElement = container.querySelector('.inner .layer-title')
	if (layerTitleElement) {
		setHeaderPopupTitle(container, layerTitleElement.innerText)
		layerTitleElement.parentElement.removeChild(layerTitleElement)
	}
}

const loadCustomWebComponents = async (content) => {
	await loadWebComponents(
		[...content.querySelectorAll('[data-element]')]
			.map((el) => el.tagName.toLowerCase())
			.filter((el, i, array) => array.indexOf(el) === i)
	)
}

const loadYTosHelpers = async (content) => {
	if (!content.querySelector('[data-ytos-ctrl]')) {
		return
	}

	await new Promise((resolve) => {
		const initHelpers = () => {
			yTos.watchOne({
				'yTos::HelpersFinishedLoading': resolve,
			})

			yTos.helpers.initializeHelpers(yTos.domlib(content), {
				shouldInitializeOnlyNew: true,
			})

			if (yTos.helpers.loading === 0) {
				resolve()
			}
		}

		if (yTos.helpers.loading === 0) {
			initHelpers()
		} else {
			yTos.watchOne({
				'yTos::HelpersFinishedLoading': initHelpers,
			})
		}
	})
}

/**
 * @param {String} contentURL
 * @param {Object} options
 */
const getAsyncContent = async (contentURL, options) => {
	try {
		const response = await yTos.fetch(contentURL, options)
		return await response.text()
	} catch (err) {
		return err.message
	}
}

const commonPopupOpen = async (mfpInstance, beforeLoad, afterLoad) => {
	mfpInstance.content[0].classList.add('opening')
	setLoader(mfpInstance.content[0])

	createHeaderPopup(mfpInstance.content[0], mfpInstance.container[0])
	tryResolveHeaderPopupTitle(mfpInstance.container[0])

	await beforeLoad?.()
	if (!mfpInstance.content) return
	await loadCustomWebComponents(mfpInstance.content[0])
	await loadYTosHelpers(mfpInstance.content[0])
	await afterLoad?.()

	yTos.notify('yTos::LayerOpened', mfpInstance.container[0])
	yTos.notify('CAR::UpdateFloatingLabel')
	// yTos.notify('CAR::PauseAllVideoAndAnimations')

	mfpInstance.container[0].focus()
	mfpInstance.container[0].parentElement?.scrollTo(0, 0)

	removeLoader(mfpInstance.content[0])
	mfpInstance.content[0].classList.remove('opening')
}

/**
 * @param {Object} opt - Popup options
 * @param {Array} opt.cssClasses Array of css classes. Default: ['size-small']
 * @param {String} opt.title The title string in the header. Default: inherit from layer-title inner text
 * @param {String} opt.content The static content. Default null
 * @param {String} opt.contentURL The URL to load dynamic content. Default: null
 */
const popup = (opt = {}) => {
	const OPTIONS = {
		cssClasses: opt.cssClasses || ['size-medium'],
		title: opt.title,
		content: opt.content || '',
		contentURL: opt.contentURL || null,
		contentURLOptions: opt.contentURLOptions || {},
		onOpen: opt.onOpen,
		onClose: opt.onClose,
	}

	window.require(['ui.layer'], () => {
		yTos.notify('yTos::OpenLayer', {
			cssClasses: OPTIONS.cssClasses,
			content: OPTIONS.content,
			title: OPTIONS.title,
			options: {
				callbacks: {
					async open() {
						const afterLoad = () =>
							OPTIONS.onOpen?.(this.container[0], OPTIONS.onOpenOptions)

						const beforeLoad = async () => {
							if (OPTIONS.title) setHeaderPopupTitle(this.container[0], OPTIONS.title)
							this.dataContentAppended = false

							if (OPTIONS.contentURL) {
								const data = await getAsyncContent(
									OPTIONS.contentURL,
									OPTIONS.contentURLOptions
								)
								if (!this.content) return

								const doc = document.createRange().createContextualFragment(data)
								const popupInnerContent = this.content[0].querySelector(
									'[data-ytos-accessible-popup-content]'
								)
								popupInnerContent.textContent = ''
								popupInnerContent.appendChild(doc)

								tryResolveHeaderPopupTitle(this.container[0])
							}
							this.dataContentAppended = true
						}

						await commonPopupOpen(this, beforeLoad, afterLoad)
					},
					beforeClose() {
						if (!this.dataContentAppended)
							yTos.notify('CAR::FetchWrapper::Abort', null, { scope: 'ALL' })
					},
					afterClose() {
						// yTos.notify('CAR::PlayAllVideoAndAnimations')
						yTos.notify('yTos::LayerClosed')
						OPTIONS.onClose?.()
					},
				},
			},
		})
	})
}

const setLayerConfiguration = () => {
	yTos.configuration.ui = {
		...yTos.configuration.ui,
		...{
			layer: {
				showCloseBtn: false,
				autoFocusLast: true,
				removalDelay: 222,
				callbacks: {
					beforeOpen() {
						yTos.Cartier.overlay.block(this.container[0])

						this.wrap[0].setAttribute('role', 'dialog')
						this.wrap[0].setAttribute('aria-modal', 'true')
						this.wrap[0].setAttribute('aria-labelledby', 'layer-title__spanTitle')
					},
					async open() {
						await commonPopupOpen(this)
					},
					close() {
						yTos.helpers.destroyPlugins(this.content)
						yTos.Cartier.overlay.unBlock()
					},
				},
			},
		},
	}
}

export { popup, setLayerConfiguration }
